import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { translatePaymentStatus } from '../../utils/statusLabels';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, RefreshCw, Gift, Users, DollarSign, 
  Search, Filter, Percent, Package, Calendar
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCommissions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reservations, setReservations] = useState([]);
  const [manualReservations, setManualReservations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCoupon, setSelectedCoupon] = useState('all');

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [resResponse, manualResponse, pkgResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reservations/`),
        axios.get(`${BACKEND_URL}/api/manual-reservations/`),
        axios.get(`${BACKEND_URL}/api/packages/?active_only=false`)
      ]);
      
      setReservations(resResponse.data);
      setManualReservations(manualResponse.data);
      setPackages(pkgResponse.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  // Processar todas as reservas com cupom
  const commissionsData = useMemo(() => {
    const allCommissions = [];
    
    // Reservas normais - verificar tanto coupon_code quanto discount_coupon (campo legado)
    reservations.forEach(res => {
      const couponCode = res.coupon_code || res.discount_coupon;
      if (couponCode) {
        const pkg = packages.find(p => p.id === res.package_id);
        allCommissions.push({
          id: res.id,
          source: 'site',
          customerName: res.customer_name,
          customerEmail: res.customer_email,
          customerCpf: res.customer_cpf,
          packageName: res.package_name || pkg?.name || 'Pacote não encontrado',
          packageCategory: res.package_category || pkg?.category,
          selectedDate: res.selected_date,
          couponCode: couponCode,
          totalAmount: res.total_amount || res.final_total || 0,
          originalTotal: res.original_total || res.total_amount || 0,
          discountAmount: res.coupon_discount_amount || res.discount_amount || 0,
          pixDiscountAmount: res.pix_discount_amount || 0,
          paymentStatus: res.payment_status,
          createdAt: res.created_at
        });
      }
    });
    
    // Reservas manuais - verificar tanto coupon_code quanto discount_coupon (campo legado)
    manualReservations.forEach(res => {
      const couponCode = res.coupon_code || res.discount_coupon;
      if (couponCode) {
        const pkg = packages.find(p => p.id === res.package_id);
        allCommissions.push({
          id: res.id,
          source: 'manual',
          customerName: res.customer_name,
          customerEmail: res.customer_email,
          customerCpf: res.customer_cpf,
          packageName: res.package_name || pkg?.name || 'Pacote não encontrado',
          packageCategory: res.package_category || pkg?.category,
          selectedDate: res.selected_date,
          couponCode: couponCode,
          totalAmount: res.total_amount || 0,
          originalTotal: res.original_total || res.total_amount || 0,
          discountAmount: res.coupon_discount_amount || res.discount_amount || (res.amount_paid ? (res.total_amount - res.amount_paid) : 0),
          pixDiscountAmount: res.pix_discount_amount || 0,
          paymentStatus: res.status,
          createdAt: res.created_at
        });
      }
    });
    
    return allCommissions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [reservations, manualReservations, packages]);

  // Lista de cupons únicos
  const uniqueCoupons = useMemo(() => {
    const coupons = new Set();
    commissionsData.forEach(c => coupons.add(c.couponCode));
    return Array.from(coupons).sort();
  }, [commissionsData]);

  // Filtrar por cupom e busca
  const filteredCommissions = useMemo(() => {
    return commissionsData.filter(c => {
      // Filtro por cupom
      if (selectedCoupon !== 'all' && c.couponCode !== selectedCoupon) return false;
      
      // Filtro por busca
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          c.customerName?.toLowerCase().includes(search) ||
          c.customerEmail?.toLowerCase().includes(search) ||
          c.couponCode?.toLowerCase().includes(search) ||
          c.packageName?.toLowerCase().includes(search)
        );
      }
      
      return true;
    });
  }, [commissionsData, selectedCoupon, searchTerm]);

  // Estatísticas
  const stats = useMemo(() => {
    const filtered = selectedCoupon === 'all' ? commissionsData : commissionsData.filter(c => c.couponCode === selectedCoupon);
    return {
      totalCommissions: filtered.length,
      totalValue: filtered.reduce((sum, c) => sum + c.totalAmount, 0),
      totalDiscount: filtered.reduce((sum, c) => sum + c.discountAmount, 0),
      uniqueCoupons: uniqueCoupons.length
    };
  }, [commissionsData, selectedCoupon, uniqueCoupons]);

  // Agrupar por cupom para resumo
  const couponSummary = useMemo(() => {
    const summary = {};
    commissionsData.forEach(c => {
      if (!summary[c.couponCode]) {
        summary[c.couponCode] = {
          code: c.couponCode,
          count: 0,
          totalValue: 0,
          totalDiscount: 0
        };
      }
      summary[c.couponCode].count++;
      summary[c.couponCode].totalValue += c.totalAmount;
      summary[c.couponCode].totalDiscount += c.discountAmount;
    });
    return Object.values(summary).sort((a, b) => b.count - a.count);
  }, [commissionsData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="hover:text-[#FFD700]">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <Gift className="w-6 h-6" />
            <h1 className="text-xl font-bold">Comissões - Cupons de Desconto</h1>
          </div>
          <Button onClick={loadData} variant="outline" size="sm" className="text-white border-white hover:bg-white/10">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Cards de Resumo */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Gift className="w-5 h-5 text-purple-600" />
                <span className="text-sm text-purple-700 font-medium">Cupons Ativos</span>
              </div>
              <p className="text-2xl font-bold text-purple-700">{stats.uniqueCoupons}</p>
            </CardContent>
          </Card>

          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-700 font-medium">Usos de Cupom</span>
              </div>
              <p className="text-2xl font-bold text-green-700">{stats.totalCommissions}</p>
            </CardContent>
          </Card>

          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-blue-700 font-medium">Valor Total Vendas</span>
              </div>
              <p className="text-2xl font-bold text-blue-700">{formatPrice(stats.totalValue)}</p>
            </CardContent>
          </Card>

          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Percent className="w-5 h-5 text-orange-600" />
                <span className="text-sm text-orange-700 font-medium">Total Descontos</span>
              </div>
              <p className="text-2xl font-bold text-orange-700">{formatPrice(stats.totalDiscount)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filtros */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                <Search className="w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Buscar por cliente, email, pacote..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-gray-400" />
                <Select value={selectedCoupon} onValueChange={setSelectedCoupon}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filtrar por cupom" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os cupons</SelectItem>
                    {uniqueCoupons.map(coupon => (
                      <SelectItem key={coupon} value={coupon}>{coupon}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Resumo por Cupom */}
        {couponSummary.length > 0 && (
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Gift className="w-5 h-5" />
                Resumo por Cupom
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {couponSummary.map(coupon => (
                  <div 
                    key={coupon.code}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedCoupon === coupon.code 
                        ? 'bg-purple-100 border-purple-400' 
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                    onClick={() => setSelectedCoupon(selectedCoupon === coupon.code ? 'all' : coupon.code)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-purple-600 text-white font-mono text-sm">
                        {coupon.code}
                      </Badge>
                      <span className="text-sm text-gray-500">{coupon.count} uso(s)</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Vendas:</span>
                        <span className="font-medium text-green-700">{formatPrice(coupon.totalValue)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Descontos:</span>
                        <span className="font-medium text-orange-700">{formatPrice(coupon.totalDiscount)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lista de Comissões */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Clientes com Cupom ({filteredCommissions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredCommissions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Gift className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <h3 className="text-lg font-medium mb-2">Nenhum uso de cupom encontrado</h3>
                <p className="text-sm">
                  {commissionsData.length === 0 
                    ? 'Ainda não há reservas com cupom de desconto.'
                    : 'Nenhuma reserva corresponde aos filtros selecionados.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-semibold">Cliente</th>
                      <th className="text-left p-3 font-semibold">Cupom</th>
                      <th className="text-left p-3 font-semibold">Pacote</th>
                      <th className="text-left p-3 font-semibold">Data Viagem</th>
                      <th className="text-right p-3 font-semibold">Valor</th>
                      <th className="text-right p-3 font-semibold">Desconto</th>
                      <th className="text-center p-3 font-semibold">Status</th>
                      <th className="text-left p-3 font-semibold">Data Reserva</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCommissions.map((commission) => (
                      <tr key={commission.id} className="border-b hover:bg-gray-50">
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{commission.customerName}</p>
                            <p className="text-sm text-gray-500">{commission.customerEmail}</p>
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge className="bg-purple-100 text-purple-800 font-mono">
                            {commission.couponCode}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-gray-400" />
                            <span>{commission.packageName}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span>{commission.selectedDate || '-'}</span>
                          </div>
                        </td>
                        <td className="p-3 text-right font-medium text-green-700">
                          {formatPrice(commission.totalAmount)}
                        </td>
                        <td className="p-3 text-right font-medium text-orange-700">
                          {commission.discountAmount > 0 ? formatPrice(commission.discountAmount) : '-'}
                        </td>
                        <td className="p-3 text-center">
                          <Badge className={
                            commission.paymentStatus === 'paid' || commission.paymentStatus === 'pago_total'
                              ? 'bg-green-100 text-green-800'
                              : commission.paymentStatus === 'pago_parcial'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }>
                            {commission.paymentStatus === 'paid' || commission.paymentStatus === 'pago_total' ? 'Pago' :
                             commission.paymentStatus === 'pago_parcial' ? 'Parcial' : 
                             translatePaymentStatus(commission.paymentStatus) || 'Pendente'}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm text-gray-600">
                          {formatDate(commission.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminCommissions;
